import { onBeforeUnmount, ref } from 'vue'
import { PLACING_STEPS } from '../constants/investFlow'

// Pace of the Placing Order checklist while the request is in flight, and
// how long the finished checklist stays up before Order Confirmed.
const STEP_INTERVAL_MS = 700
const SETTLE_DELAY_MS = 450
// The last step ("Placing Order") only ticks when the server answers.
const LAST_PENDING_STEP = PLACING_STEPS.length - 1

/**
 * Drives the Placing Order checklist around a real request: steps tick on a
 * timer up to the final one, which waits for the server. `run` resolves with
 * the request's result once the checklist has visibly completed, and rejects
 * straight away if the request fails.
 */
export const usePlacementProgress = () => {
  const activeStep = ref(0)
  let timer: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }

  const run = async <T>(request: () => Promise<T>): Promise<T> => {
    stop()
    activeStep.value = 0
    timer = setInterval(() => {
      if (activeStep.value < LAST_PENDING_STEP) activeStep.value++
    }, STEP_INTERVAL_MS)
    try {
      const result = await request()
      stop()
      activeStep.value = PLACING_STEPS.length
      await new Promise((resolve) => setTimeout(resolve, SETTLE_DELAY_MS))
      return result
    } finally {
      stop()
    }
  }

  onBeforeUnmount(stop)

  return { activeStep, run }
}
